import {maybeScaleDownImage} from "./maybe-scale-down-image.ts";

/**
 * Converts an HTMLImageElement or HTMLVideoElement to a scaled PNG data URL.
 * This function ensures consistent image processing across different image input methods
 * by scaling down large images and converting to PNG format.
 */
export function convertImageToDataURL(
    source: HTMLImageElement | HTMLVideoElement,
): string {
    // Get the height and width of the source
    // so that we can preserve the aspect ratio
    const height =
        source instanceof HTMLImageElement
            ? source.naturalHeight
            : source.videoHeight;
    const width =
        source instanceof HTMLImageElement
            ? source.naturalWidth
            : source.videoWidth;

    // No point in submitting images with no actual area.
    if (height === 0 || width === 0) {
        throw new Error("Image has zero height or width");
    }

    const {targetWidth, targetHeight} = maybeScaleDownImage(width, height);

    // Create a temporary canvas for rendering
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (ctx === null) {
        throw new Error("Canvas context is null");
    }

    try {
        // Set up a clean, correctly sized canvas for rendering.
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Draw the scaled image/video onto the canvas
        // Suppress error because we know that drawImage has a
        // signature that accepts an Image or Video object and 4 numbers
        // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage#syntax
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- maintaining consistency with existing code
        // @ts-ignore
        ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

        // Extract image from canvas as PNG data URL
        return canvas.toDataURL("image/png");
    } catch (err) {
        throw new Error(`Error rendering image: ${err}`);
    }
}
